import { AnimatePresence, motion } from 'framer-motion'
import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import { useGetUnitInactiveById } from '@apps-common/apis/portal/unit'
import { useCreateUnitActivation, useGetUnitActivationById, useUpdateUnitActivation } from '@apps-common/apis/portal/unit-activations'
import {
  UnitActivationConfirmationForm,
  UnitActivationPlanForm
} from '@apps-common/apps/portal/pages/units/pages/activations/pages/activation-modify/components'
import {
  UnitActivationCompanyForm,
  UnitActivationUnitForm
} from '@apps-common/apps/portal/pages/units/pages/activations/pages/activation-modify/custom-activation'
import {
  ModifyUnitActivationStep,
  stepDirection
} from '@apps-common/apps/portal/pages/units/pages/activations/pages/activation-modify/Index'
import { VertSpacerXl } from '@apps-common/components/layout'
import { commonIcons } from '@apps-common/config/icons'
import { motionGrowFadeIn } from '@apps-common/config/motion-variants'
import { UnitActivationStatus } from '@apps-common/config/unit-activation'
import { useGarbageCollector } from '@apps-common/contexts/GarbageContext'
import { useNavigation } from '@apps-common/hooks/useNavigation'
import { IActivationServiceRequestCreateModel } from '@apps-common/types/portal/service-request'
import { IUnitGenericDetailsModel } from '@apps-common/types/portal/unit'

import { UnitPlanUpgradeability } from '@apps-common/config/unit-plan'
import { afterActivationUpdate } from '../helpers/afterActivationUpdate'

interface IProps {
  unitActivationId?: number | null
  defaultUnit?: IUnitGenericDetailsModel
}

export const UnitActivationCustomModifyView: React.FC<IProps> = ({ unitActivationId, defaultUnit }) => {
  const [step, setStep] = useState<ModifyUnitActivationStep>(ModifyUnitActivationStep.companies)
  const { navigateReplace } = useNavigation()
  const { purge } = useGarbageCollector()

  const isEditing = !Number.isNaN(unitActivationId)

  const moveStep = (direction: stepDirection) => {
    if (direction === 'forward') {
      if (step === ModifyUnitActivationStep.companies) {
        setStep(ModifyUnitActivationStep.unit)

        return ModifyUnitActivationStep.unit
      }
      if (step === ModifyUnitActivationStep.unit) {
        setStep(ModifyUnitActivationStep.plan)

        return ModifyUnitActivationStep.plan
      }
      if (step === ModifyUnitActivationStep.plan) {
        setStep(ModifyUnitActivationStep.confirm)

        return ModifyUnitActivationStep.confirm
      }
      if (step === ModifyUnitActivationStep.confirm) {
        return ModifyUnitActivationStep.submit
      }
    } else if (direction === 'backward') {
      if (step === ModifyUnitActivationStep.confirm) {
        setStep(ModifyUnitActivationStep.plan)

        return ModifyUnitActivationStep.plan
      }
      if (step === ModifyUnitActivationStep.plan) {
        setStep(ModifyUnitActivationStep.unit)

        return ModifyUnitActivationStep.unit
      }
      if (step === ModifyUnitActivationStep.unit) {
        setStep(ModifyUnitActivationStep.companies)

        return ModifyUnitActivationStep.companies
      }
    }

    return step
  }

  const {
    isLoading: activationIsLoading,
    isError: activationIsError,
    data: activation
  } = useGetUnitActivationById({
    config: { enabled: isEditing },
    id: unitActivationId || 0
  })

  const {
    data: activationUnit,
    isLoading: activationUnitIsLoading,
    isError: activationUnitIsError
  } = useGetUnitInactiveById({
    config: { retry: false, enabled: !!activation },
    id: activation?.unitId || ''
  })

  const { mutate: createActivation, isPending: createActivationIsPending } = useCreateUnitActivation(true, (data) =>
    afterActivationUpdate(data, navigateReplace, purge)
  )
  const { mutate: updateActivation, isPending: updateActivationIsPending } = useUpdateUnitActivation(true, (data) =>
    afterActivationUpdate(data, navigateReplace, purge)
  )

  const methods = useForm<IActivationServiceRequestCreateModel>({
    mode: 'onChange',
    reValidateMode: 'onChange'
  })
  const { setValue, handleSubmit, reset } = methods

  useEffect(() => {
    if (activation) {
      const clone = { ...activation }

      if (!clone.assignBureau) {
        clone.bureauId = ''
        clone.bureau = ''
      }

      if (!clone.assignTech) {
        clone.techId = ''
        clone.tech = ''
      }

      reset(clone)
    }
  }, [activation])

  useEffect(() => {
    if (activationUnit) {
      const { unitId, serialNo, imei, imageUrl, productId, product, planUpgradePath, planId, planName } = activationUnit
      setValue('unitPlanUpgradePath', activationUnit.planUpgradePath, { shouldValidate: true })
      setValue('unitId', unitId, { shouldValidate: true })
      setValue('serialNo', serialNo, { shouldValidate: true })
      setValue('imei', imei, { shouldValidate: true })
      setValue('productImageUrl', imageUrl, { shouldValidate: true })
      setValue('productId', productId, { shouldValidate: true })
      setValue('productName', product, { shouldValidate: true })
      setValue('unitPlanUpgradePath', planUpgradePath, { shouldValidate: true })

      if (planUpgradePath === UnitPlanUpgradeability.fixed) {
        setValue('unitPlanId', planId, { shouldValidate: true })
        setValue('unitPlanName', planName, { shouldValidate: true })
      }
    }
  }, [activationUnit])

  const onSubmit = async (values: IActivationServiceRequestCreateModel) => {
    const nextStep = moveStep('forward')

    if (nextStep !== ModifyUnitActivationStep.submit) return

    if (!isEditing) {
      createActivation({ model: values })
    } else {
      updateActivation({ model: values })
    }
  }

  const loading =
    isEditing &&
    (activationIsLoading || activationIsError || activationUnitIsLoading || activation?.status !== UnitActivationStatus.cancelled)
  const updating = createActivationIsPending || updateActivationIsPending

  return (
    <div className='max-w-[800px]'>
      <AnimatePresence>
        {activation && activation.status !== UnitActivationStatus.cancelled && (
          <motion.div {...motionGrowFadeIn(0.25)} className='sm:flex items-center justify-start mb-[25px]'>
            <div className='rounded-2xl px-[20px] py-[20px] bg-warning text-on-warning'>
              <div className='flex items-center justify-center gap-[10px]'>
                <div className='p-3 rounded-full bg-warning'>
                  <commonIcons.warning className='text-[1.3rem]' />
                </div>
                <div className='flex flex-col gap-[5px]'>
                  <div>Activation status invalid</div>
                  <div className='text-[0.8em]'>
                    Cannot update when status is <span className='jost-bold'>{activation.status}</span>. Activations can only be updated
                    when in <span className='jost-bold'>cancelled</span> status.
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <FormProvider {...methods}>
        <form noValidate onSubmit={handleSubmit(onSubmit)}>
          {/* step 1 - company */}
          <UnitActivationCompanyForm loading={loading} updating={updating} step={step} moveStep={moveStep} />
          <VertSpacerXl />
          {/* step 2 - unit */}
          <UnitActivationUnitForm
            stepNo={2}
            loading={loading}
            updating={updating}
            step={step}
            moveStep={moveStep}
            defaultUnit={defaultUnit}
          />
          <VertSpacerXl />
          {/* step 3 - plan */}
          <UnitActivationPlanForm stepNo={3} loading={loading} updating={updating} step={step} moveStep={moveStep} />
          <VertSpacerXl />
          {/* step 4 - confirm */}
          <UnitActivationConfirmationForm stepNo={4} loading={loading} updating={updating} step={step} moveStep={moveStep} />
        </form>
      </FormProvider>
      <VertSpacerXl />
    </div>
  )
}
